import React, { useState, useEffect } from "react";
import axios from "axios";
import PageShell from "../components/PageShell";
import "./Memories.css";

const AdminMemories = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [space, setSpace] = useState("all");

  useEffect(() => {
    axios
      .get("/api/admin/users", { withCredentials: true })
      .then((res) => setEmployees(res.data || []))
      .catch((err) => console.error("Failed to fetch employees", err));
  }, []);

  useEffect(() => {
    if (!selectedUserId) { setMemories([]); return; }
    fetchMemories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, space, search]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/api/admin/memories?user_id=${selectedUserId}&space=${space}&search=${encodeURIComponent(search)}`,
        { withCredentials: true }
      );
      setMemories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch memories", err);
    } finally {
      setLoading(false);
    }
  };

  const selectedEmployee = employees.find((e) => e.id === selectedUserId);

  return (
    <PageShell
      title="Employee Moments"
      subtitle="View all moments logged by any employee."
    >
      {/* Controls */}
      <div className="memories-header">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select
            className="input"
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setSearch("");
              setSpace("all");
            }}
            style={{ minWidth: 220, padding: "10px 16px", borderRadius: 12, border: "1px solid #e5e7eb" }}
          >
            <option value="">-- Select an employee --</option>
            {employees
              .filter((e) => e.role !== "company_admin")
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name || e.email} ({e.role === "team_manager" ? "Manager" : "Employee"})
                </option>
              ))}
          </select>

          {selectedUserId && (
            <div className="space-toggle">
              {[
                { value: "all", label: "All" },
                { value: "personal", label: "Personal" },
                { value: "shared", label: "Mutual Wins" },
              ].map((s) => (
                <button
                  key={s.value}
                  className={`toggle-btn ${space === s.value ? "active" : ""}`}
                  onClick={() => setSpace(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedUserId && (
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search titles, content, or tags..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Results */}
      {!selectedUserId && (
        <div className="empty-state">
          <p>Select an employee above to view their moments.</p>
        </div>
      )}

      {selectedUserId && loading && (
        <div className="loading-state">Loading moments...</div>
      )}

      {selectedUserId && !loading && (
        <div className="memories-grid">
          {memories.length > 0 ? (
            memories.map((m) => (
              <div key={m.id} className={`memory-card ${m.visibility} ${m.status}`}>
                <div className="memory-card__header">
                <span className={`visibility-badge ${m.visibility} ${m.status}`}>
                    {m.status === "pending" ? "Pending Approval" : m.visibility}
                </span>
                {m.visibility === "shared" && (
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      with {m.creator_id === selectedUserId ? m.recipient_name : m.creator_name}
                    </span>
                )}
                </div>

                <h3 className="memory-card__title">{m.title}</h3>

                <p className="memory-card__content">{m.content}</p>

                <div className="memory-card__tags">
                  {m.tags?.map((tag) => (
                    <span key={tag} className="tag" onClick={() => setSearch(tag)}>
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="memory-card__footer">
                  <span className="creator">By {m.creator_name}</span>
                  <span className="date">{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No moments found for {selectedEmployee?.name || "this employee"}.</p>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
};

export default AdminMemories;